/**
 * Build-time client version surfaced from window.__SV_ENV.VITE_CLIENT_VERSION,
 * which is populated in www/src/react/main.tsx from the `CLIENT_VERSION` env
 * var set by the GitLab CI deploy jobs via Vite's `define` (see
 * vite.config.ts). The value is empty for local dev and for ad-hoc CI builds
 * that are not part of the release flow.
 *
 * When non-empty, the value is sent on every backend request as the
 * `skymap-client-version` header so the backend can detect a stale client and
 * respond with `X-Skymap-Client-Stale: 1` (see SK-5052 / SK-7015). Empty
 * versions are omitted from the header and the backend ignores such requests.
 *
 * We previously used `Clear-Site-Data: "*"` for the stale signal, but that
 * header is credentialed-mode-only (our axios instance does not opt in) and
 * its `executionContexts` directive would tear down the page before the
 * reload toast could paint. A plain custom header avoids both problems.
 *
 * An org-admin-only dev tool (see /dev-tools/client-version) can write an
 * override into localStorage to force a specific version for manual testing
 * of the mismatch flow. The override takes precedence over the build-time
 * value. The axios response interceptor clears the override whenever the
 * stale signal is observed so a mandatory reload does not loop.
 */
import { getViteEnv } from '../../utils/environment/vite-env';

export const CLIENT_VERSION_HEADER = 'skymap-client-version';

/**
 * Response header emitted by the backend when it detects that the client is
 * running an older version than the deployed one (value `'1'`). The frontend
 * reads it on every axios response and triggers the reload toast on match.
 * Axios lowercases incoming header names, so this constant is lowercase too.
 */
export const CLIENT_STALE_RESPONSE_HEADER = 'x-skymap-client-stale';

const OVERRIDE_STORAGE_KEY = 'skymap.clientVersionOverride';

/** Read the current override, or null when none is set / storage is unavailable. */
export const getClientVersionOverride = (): string | null => {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  try {
    return localStorage.getItem(OVERRIDE_STORAGE_KEY);
  } catch {
    return null;
  }
};

/** Persist a manual override. Empty strings are treated as "clear". */
export const setClientVersionOverride = (value: string): void => {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    if (value === '') {
      localStorage.removeItem(OVERRIDE_STORAGE_KEY);
    } else {
      localStorage.setItem(OVERRIDE_STORAGE_KEY, value);
    }
  } catch {
    /* storage quota / private mode — silently ignore */
  }
};

/** Drop any manual override. Safe to call when none is set. */
export const clearClientVersionOverride = (): void => {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(OVERRIDE_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

/** Read the effective client version: override if present, else build-time value. */
export const getClientVersion = (): string =>
  getClientVersionOverride() ?? getViteEnv('VITE_CLIENT_VERSION') ?? '';
